/**
 * Reaction Batch Loader
 *
 * Coalesces every ReactionTarget load on a page into ONE batched
 * request per target type, replacing the 50+ individual
 * GET /api/reactions calls a Spaces conversation used to fire.
 *
 * Primary responsibilities:
 * - Collect targets during a short coalesce window, then flush
 *   them as a single /api/reactions/batch request
 * - Keep the per-target guards from the flood fix: in-flight
 *   dedupe and a 30s backoff after a transport failure, so a
 *   dead target cannot rejoin every realtime refresh wave
 * - Yield to critical loads: reactions are metadata below the
 *   messages, so the coalesce window also lets space/chat
 *   requests issued in the same commit go first
 *
 * Dependencies:
 * - lib/auth/authenticated-fetch (circuit-breaker protected,
 *   loaded lazily so this module stays importable anywhere)
 */

export type ReactionBatchTargetType =
  | 'message'
  | 'space_conversation_message'
  | 'space_task_comment'

export type ApiReactionSummary = {
  emoji: string
  count: number
  reactedByUser: boolean
}

export type ReactionBatchTarget = {
  targetType: ReactionBatchTargetType
  targetId: string
}

type Fetcher = (
  input: string,
  init?: { signal?: AbortSignal }
) => Promise<{ ok: boolean; json: () => Promise<unknown> }>

async function defaultFetcher(
  input: string,
  init?: { signal?: AbortSignal }
) {
  const { authenticatedFetch } = await import(
    '@/lib/auth/authenticated-fetch'
  )
  return authenticatedFetch(input, init)
}

// Reactions are never worth a retry storm: after a transport
// failure a target sits out 30s (realtime refreshes it later).
const FAILED_TARGET_BACKOFF_MS = 30_000
// Mounts in one render commit land within this window and share
// a request. It also defers reactions behind the space/chat
// requests from the same commit, which the composer waits on.
const BATCH_COALESCE_MS = 50
const MAX_BATCH_TARGETS = 100

const failures = new Map<string, number>()
const inFlight = new Map<string, Promise<ApiReactionSummary[] | null>>()

type Waiter = {
  resolve: (value: ApiReactionSummary[] | null) => void
  signal?: AbortSignal
  settled: boolean
}

type PendingGroup = {
  entries: Map<string, { target: ReactionBatchTarget; waiters: Waiter[] }>
  timer: ReturnType<typeof setTimeout> | null
}

const pendingGroups = new Map<ReactionBatchTargetType, PendingGroup>()

const targetKey = (target: ReactionBatchTarget) =>
  `${target.targetType}:${target.targetId}`

// --------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readBatchPayload(
  payload: unknown
): Map<string, ApiReactionSummary[]> | null {
  if (!isRecord(payload) || !isRecord(payload.reactionsByTarget)) {
    return null
  }

  const result = new Map<string, ApiReactionSummary[]>()

  for (const [targetId, reactions] of Object.entries(payload.reactionsByTarget)) {
    if (!Array.isArray(reactions)) continue

    result.set(
      targetId,
      reactions.filter(
        (reaction): reaction is ApiReactionSummary =>
          isRecord(reaction) &&
          typeof reaction.emoji === 'string' &&
          typeof reaction.count === 'number' &&
          typeof reaction.reactedByUser === 'boolean'
      )
    )
  }

  return result
}

// --------------

async function fetchBatch(
  targetType: ReactionBatchTargetType,
  targets: ReactionBatchTarget[],
  signal: AbortSignal,
  fetcher: Fetcher
): Promise<Map<string, ApiReactionSummary[]> | null> {
  const params = new URLSearchParams({
    targetType,
    targetIds: targets.map(target => target.targetId).join(',')
  })

  try {
    const response = await fetcher(`/api/reactions/batch?${params.toString()}`, {
      signal
    })

    if (!response.ok) {
      return null
    }

    return readBatchPayload(await response.json())
  } catch {
    return null
  }
}

// --------------

function settleWaiters(
  waiters: Waiter[],
  value: ApiReactionSummary[] | null
) {
  for (const waiter of waiters) {
    if (waiter.settled) continue
    waiter.settled = true
    waiter.resolve(value)
  }
}

// --------------

async function flushGroup(
  targetType: ReactionBatchTargetType,
  group: PendingGroup,
  fetcher: Fetcher
) {
  pendingGroups.delete(targetType)

  const entries = [...group.entries.values()]

  for (let offset = 0; offset < entries.length; offset += MAX_BATCH_TARGETS) {
    const chunk = entries.slice(offset, offset + MAX_BATCH_TARGETS)
    const active = chunk.filter(entry =>
      entry.waiters.some(waiter => !waiter.settled)
    )

    // Every waiter for these entries already aborted pre-flush:
    // release the in-flight slot so a remount can load again.
    for (const entry of chunk) {
      if (!active.includes(entry)) {
        inFlight.delete(targetKey(entry.target))
      }
    }

    if (active.length === 0) continue

    const controller = new AbortController()
    const abortIfAllSettled = () => {
      if (active.every(entry => entry.waiters.every(waiter => waiter.settled))) {
        controller.abort()
      }
    }

    for (const entry of active) {
      for (const waiter of entry.waiters) {
        waiter.signal?.addEventListener('abort', abortIfAllSettled, {
          once: true
        })
      }
    }

    const payload = await fetchBatch(
      targetType,
      active.map(entry => entry.target),
      controller.signal,
      fetcher
    )

    for (const entry of active) {
      const key = targetKey(entry.target)

      if (payload) {
        failures.delete(key)
        // Targets omitted by the server (unauthorized/missing)
        // render with no reactions, same as an empty result.
        settleWaiters(entry.waiters, payload.get(entry.target.targetId) ?? [])
      } else {
        failures.set(key, Date.now())
        settleWaiters(entry.waiters, null)
      }

      inFlight.delete(key)

      for (const waiter of entry.waiters) {
        waiter.signal?.removeEventListener('abort', abortIfAllSettled)
      }
    }
  }
}

// --------------

/**
 * Load one target's reactions, batched with every other target
 * requested in the same window. Resolves null when the load was
 * skipped (backoff) or failed — callers keep their last counts.
 */
export function loadReactionsBatched(
  target: ReactionBatchTarget,
  options?: { signal?: AbortSignal; fetcher?: Fetcher }
): Promise<ApiReactionSummary[] | null> {
  const key = targetKey(target)

  if (options?.signal?.aborted) {
    return Promise.resolve(null)
  }

  const failedAt = failures.get(key)
  if (failedAt && Date.now() - failedAt < FAILED_TARGET_BACKOFF_MS) {
    return Promise.resolve(null)
  }

  const existing = inFlight.get(key)
  if (existing) {
    return existing
  }

  const fetcher = options?.fetcher ?? defaultFetcher

  const promise = new Promise<ApiReactionSummary[] | null>(resolve => {
    let group = pendingGroups.get(target.targetType)

    if (!group) {
      group = { entries: new Map(), timer: null }
      pendingGroups.set(target.targetType, group)
    }

    const waiter: Waiter = {
      resolve,
      signal: options?.signal,
      settled: false
    }

    const onAbort = () => {
      if (waiter.settled) return
      waiter.settled = true
      resolve(null)
    }

    options?.signal?.addEventListener('abort', onAbort, { once: true })

    const existingEntry = group.entries.get(key)

    if (existingEntry) {
      existingEntry.waiters.push(waiter)
    } else {
      group.entries.set(key, { target, waiters: [waiter] })
    }

    if (!group.timer) {
      const scheduledGroup = group
      scheduledGroup.timer = setTimeout(() => {
        scheduledGroup.timer = null
        void flushGroup(target.targetType, scheduledGroup, fetcher)
      }, BATCH_COALESCE_MS)
    }
  })

  inFlight.set(key, promise)

  return promise
}

// --------------

/** Test hook: reset module-level batch/guard state between cases. */
export function resetReactionBatchStateForTests() {
  failures.clear()
  inFlight.clear()

  for (const group of pendingGroups.values()) {
    if (group.timer) {
      clearTimeout(group.timer)
    }

    for (const entry of group.entries.values()) {
      settleWaiters(entry.waiters, null)
    }
  }

  pendingGroups.clear()
}
